import type { Sink } from 'relay-runtime/lib/network/RelayObservable';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { GraphQLMultipartResponsePayload } from './types';

function escapeRegExp(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// The boundary is fixed for the lifetime of a response while processChunk runs once per network chunk,
// so the compiled delimiter pattern is kept for the last boundary seen instead of rebuilt per chunk.
let cachedDelimiter: { boundaryString: string; pattern: RegExp } | undefined;

function getDelimiterPattern(boundaryString: string): RegExp {
	if (cachedDelimiter?.boundaryString !== boundaryString) {
		cachedDelimiter = {
			boundaryString,
			pattern: new RegExp(`(?:^|\\r\\n)${escapeRegExp(boundaryString)}`),
		};
	}

	return cachedDelimiter.pattern;
}

function getBody(part: string): string | undefined {
	// A part with a header block is "headers\r\n\r\nbody", so the split yields [headers, body] and the
	// payload is part2. A part without a header block yields a single element, part2 is undefined and
	// the whole part (part1) is the payload.
	const [part1, part2] = part.split(/\r?\n\r?\n/);

	return part2 ?? part1;
}

function processChunkCrlfAnchored(
	chunk: string,
	boundaryString: string,
	observer: Sink<GraphQLMultipartResponsePayload>,
): string {
	// RFC 2046 5.1.1: a delimiter is CRLF followed by "--" and the boundary (the CRLF is optional only
	// for the first delimiter, at the very start of the body). Matching the bare "--boundary" would
	// also match the same characters inside a JSON payload (e.g. "---" in a document body), which
	// splits and corrupts the payload. JSON strings carry escaped newlines, so a CRLF-anchored
	// delimiter does not occur inside a payload.
	const parts = chunk.split(getDelimiterPattern(boundaryString));
	let incompleteChunk = '';

	// Process all complete parts (except the last one which might be incomplete)
	for (let i = 0; i < parts.length - 1; i++) {
		const part = parts[i].trim();

		// Skip empty parts and encapsulation boundaries
		if (part && !part.startsWith('--')) {
			const body = getBody(part);

			if (body) {
				try {
					const jsonData = JSON.parse(incompleteChunk + body);
					observer.next(jsonData);
					incompleteChunk = '';
				} catch {
					// add boundary string back to response for cases when response content has boundary string
					// no error is thrown because next part might be a valid JSON object
					incompleteChunk += body + boundaryString;
				}
			}
		}
	}

	const lastPart = parts[parts.length - 1];

	// The trailing segment is only known to be complete once the next delimiter arrives, but a part
	// whose body already parses as JSON is complete: emit it now instead of holding it back until the
	// next part (or the end of the stream). Keep the trailing CRLF so the delimiter that follows is
	// still recognised.
	const trailingPart = lastPart.trim();
	if (trailingPart && !trailingPart.startsWith('--')) {
		const body = getBody(trailingPart);
		if (body) {
			try {
				observer.next(JSON.parse(incompleteChunk + body));
				return lastPart.match(/\s*$/)?.[0] ?? '';
			} catch {
				// Body is still incomplete; keep it until more data arrives.
			}
		}
	}

	// Return the last part which might be incomplete
	return incompleteChunk + lastPart;
}

/**
 * The previous implementation, used while `ai_3p_defer_multipart_response_fix` is off.
 *
 * It splits on the bare boundary string, so a payload containing it is cut apart, and a part is only
 * emitted once the next delimiter has arrived.
 */
function processChunkLegacy(
	chunk: string,
	boundaryString: string,
	observer: Sink<GraphQLMultipartResponsePayload>,
): string {
	const parts = chunk.split(boundaryString);
	let incompleteChunk = '';

	// Process all complete parts (except the last one which might be incomplete)
	for (let i = 0; i < parts.length - 1; i++) {
		const part = parts[i].trim();

		// Skip empty parts and encapsulation boundaries
		if (part && !part.startsWith('--')) {
			// Split content-type header and body
			const [part1, part2] = part.split(/\r?\n\r?\n/);

			// when response content has boundary string, part1 is the actual response
			// when response content does not have boundary string, part2 is the actual response
			const body = part2 ?? part1;

			if (body) {
				try {
					const jsonData = JSON.parse(incompleteChunk + body);
					observer.next(jsonData);
					incompleteChunk = '';
				} catch {
					// add boundary string back to response for cases when response content has boundary string
					// no error is thrown because next part might be a valid JSON object
					incompleteChunk += body + boundaryString;
				}
			}
		}
	}

	// Return the last part which might be incomplete
	return incompleteChunk + parts[parts.length - 1];
}

export function processChunk(
	chunk: string,
	boundaryString: string,
	observer: Sink<GraphQLMultipartResponsePayload>,
): string {
	if (fg('ai_3p_defer_multipart_response_fix')) {
		return processChunkCrlfAnchored(chunk, boundaryString, observer);
	}

	return processChunkLegacy(chunk, boundaryString, observer);
}
